import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Card from "./table/Card";
import PlayersPanel from "./table/PlayersPanel";
import ActionPanel from "./table/ActionPanel";
import MySeatPanelRail from "./table/MySeatPanelRail";
import { formatStackBB } from "./table/helpers";
import { ThemeContext } from "../../context/ThemeContext";

export default function PokerTableViewList({
  // stack view toggle
  stackViewMode,
  setStackViewMode, // ✅ NEW: potrzebne do toggle po kliknięciu w stack
  bbAmount,

  betting,
  currentBet,

  board,
  seats,
  seatState,
  mySeatNo,
  actionSeat,
  dealerSeat,
  sbSeat,
  bbSeat,

  handId,

  showHandResult,
  bySeat,
  winners,
  winnersSet,
  payouts,

  revealAll,
  street,
  showdownHoles,
  doShowCards,
  lastActionBySeat,

  myHole,
  mySt,
  isMyTurn,
  waitingForDealer,
  dealerStepLabel,
  isDealer,
  canAct,

  foldDisabled,
  passiveDisabled,
  aggressiveDisabled,

  passiveType,
  passiveLabel,
  aggressiveType,
  aggressiveLabel,

  hasBet,
  clampedTo,
  minAllowedTo,
  minRaiseTo,
  maxRaiseTo,
  bb,
  potVal,
  toCall,

  setRaiseTo,
  doAction,
}) {
  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === "dark";

  // ===== Responsive layout based on REAL container width =====
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(9999);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const read = () => {
      const r = el.getBoundingClientRect();
      const w = Math.round(r.width || 0);
      if (w > 0) setWrapW(w);
    };

    read();

    const RO = window.ResizeObserver;
    const ro = RO ? new RO(() => read()) : null;
    ro?.observe(el);

    window.addEventListener("resize", read);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", read);
    };
  }, []);

  // 2 kolumny utrzymujemy dość długo, 1 kolumna dopiero naprawdę wąsko
  const STACK_BREAKPOINT = 640;
  const isStacked = wrapW > 0 && wrapW < STACK_BREAKPOINT;

  // ===== Board reveal =====
  const boardShown = useMemo(() => (Array.isArray(board) ? board.slice(0, 5) : []), [board]);

  const targetByStreet = useMemo(() => {
    if (street === "preflop") return 0;
    if (street === "flop") return 3;
    if (street === "turn") return 4;
    if (street === "river") return 5;
    if (street === "showdown") return 5;
    if (street === "complete") return 5;
    return Math.min(5, boardShown.filter(Boolean).length);
  }, [street, boardShown]);

  const [boardRevealCount, setBoardRevealCount] = useState(0);

  useEffect(() => {
    if (targetByStreet === 0) {
      setBoardRevealCount(0);
      return;
    }

    setBoardRevealCount((prev) => Math.min(prev, targetByStreet));

    if (street === "flop") return void setBoardRevealCount(3);
    if (street === "turn") return void setBoardRevealCount(4);
    if (street === "river" || street === "showdown" || street === "complete") return void setBoardRevealCount(5);

    setBoardRevealCount(targetByStreet);
  }, [street, targetByStreet]);

  const renderBoardFive = () => {
    const out = [];

    for (let i = 0; i < 5; i++) {
      const c = boardShown[i];
      const isRevealed = i < boardRevealCount;

      const shouldAnimate =
        isRevealed &&
        ((street === "flop" && i < 3) || (street === "turn" && i === 3) || (street === "river" && i === 4));

      if (c && isRevealed) {
        out.push(
          <Card
            key={`lb-${i}-${street}`}
            c={c}
            size="sm"
            revealMode={shouldAnimate ? "wipeUp" : "none"}
            revealMs={3000}
            revealDelayMs={street === "flop" && i < 3 ? i * 160 : 0}
          />
        );
      } else {
        out.push(<div key={`lb-e-${i}`} className="poker-card-rect poker-card-rect--sm poker-card-rect--empty" />);
      }
    }

    return out;
  };

  // ===== Show-cards (czy moje karty są już publiczne po kliknięciu "Pokaż karty") =====
  const mySeatKey = mySeatNo != null ? String(mySeatNo) : null;
  const hasPublicCardsLocal = !!(
    mySeatKey && showdownHoles?.[mySeatKey] && Array.isArray(showdownHoles[mySeatKey]) && showdownHoles[mySeatKey].length === 2
  );

  // ===== Pot =====
  const potRaw = Number(betting?.pot ?? potVal ?? 0);
  const potText = useMemo(() => {
    if (stackViewMode === "bb") return `${formatStackBB(potRaw, bbAmount)} BB`;
    return String(potRaw);
  }, [stackViewMode, potRaw, bbAmount]);

  // Felt 1:1 jak w PokerTableVisual (dark/light)
  const feltA = isDark ? "rgba(16, 70, 48, 0.98)" : "rgba(175, 236, 196, 0.98)";
  const feltB = isDark ? "rgba(6, 42, 28, 0.98)" : "rgba(86, 186, 118, 0.98)";

  // ===== Layout =====
  const gridStyle = isStacked
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 16,
        alignItems: "start",
      }
    : {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) fit-content(240px)",
        gap: 16,
        alignItems: "start",
      };

  return (
    <div style={{ marginTop: 14 }} ref={wrapRef}>
      <div className="poker-grid" style={gridStyle}>
        {/* LEWA */}
        <div className="poker-card" style={{ minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>Lista</div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: `radial-gradient(circle at 50% 40%, ${feltA}, ${feltB} 70%)`,
                border: "2px solid rgba(255,165,0,0.20)",
                boxShadow: "0 0 0 10px rgba(0,0,0,0.12) inset, 0 0 35px rgba(0,0,0,0.25)",
                marginBottom: 12,
              }}
            >
              <div className="board-cards" style={{ marginBottom: 10 }}>
                {renderBoardFive()}
              </div>

              <div style={{ textAlign: "center", opacity: 0.92, fontWeight: 900 }}>
                pula: <span style={{ opacity: 0.98 }}>{potText}</span>
              </div>
            </div>

            <PlayersPanel
              stackViewMode={stackViewMode}
              bbAmount={bbAmount}
              setStackViewMode={setStackViewMode}
              seats={seats}
              seatState={seatState}
              mySeatNo={mySeatNo}
              actionSeat={actionSeat}
              dealerSeat={dealerSeat}
              sbSeat={sbSeat}
              bbSeat={bbSeat}
              showHandResult={showHandResult}
              bySeat={bySeat}
              winners={winners}
              winnersSet={winnersSet}
              payouts={payouts}
              revealAll={!!revealAll}
              street={street}
              showdownHoles={showdownHoles}
              onShowCards={null}
              lastActionBySeat={lastActionBySeat}
              handId={handId}
            />
          </div>
        </div>

        {/* PRAWA */}
        <div className="poker-card poker-right" style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 900 }}>Twoje karty</div>

          <div
            className="hole-cards"
            style={{
              marginTop: 12,
              display: "flex",
              flexWrap: "nowrap",
              gap: 14,
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              paddingBottom: 4,
            }}
          >
              {(myHole || []).map((c, idx) => (
                <Card
                  key={`myhole-${handId || 0}-${idx}-${c?.r || "X"}${c?.s || "X"}`}
                  c={c}
                  size="md"
                  revealMode="wipeUp"
                  revealMs={1100}
                  revealDelayMs={idx * 160}
                />
              ))}
            {!myHole?.length ? (
              <div style={{ opacity: 0.7, whiteSpace: "normal" }}>Brak kart (nie siedzisz / nie rozdano).</div>
            ) : null}
          </div>

          {/* ✅ Info jak w Visual: wspólny komponent (1 źródło prawdy) */}
          <div style={{ marginTop: 10 }}>
            <MySeatPanelRail
              bbAmount={bbAmount}
              setStackViewMode={setStackViewMode}
              stackViewMode={stackViewMode}
              mySeatNo={mySeatNo}
              mySt={mySt}
              myHole={myHole}
              isDealer={isDealer}
              sbSeat={sbSeat}
              bbSeat={bbSeat}
              isMyTurn={isMyTurn}
              showCards={false}
              // ✅ reveal button przeniesiony tutaj (Twoje karty)
              canRevealPhase={street === "showdown" || street === "complete" || !!revealAll}
              hasPublicCards={hasPublicCardsLocal}
              onShowCards={doShowCards}
              lastActionBySeat={lastActionBySeat}
              showHandResult={showHandResult}
              bySeat={bySeat}
              payouts={payouts}
            />
          </div>



          <ActionPanel
            bbAmount={bbAmount}
            stackViewMode={stackViewMode}
            mySeatNo={mySeatNo}
            myInHand={!!mySt?.inHand}
            isMyTurn={isMyTurn}
            actionSeat={actionSeat}
            waitingForDealer={waitingForDealer}
            dealerStepLabel={dealerStepLabel}
            isDealer={isDealer}
            canAct={canAct}
            foldDisabled={foldDisabled}
            passiveDisabled={passiveDisabled}
            aggressiveDisabled={aggressiveDisabled}
            passiveType={passiveType}
            passiveLabel={passiveLabel}
            aggressiveType={aggressiveType}
            aggressiveLabel={aggressiveLabel}
            hasBet={hasBet}
            clampedTo={clampedTo}
            minAllowedTo={minAllowedTo}
            minRaiseTo={minRaiseTo}
            maxRaiseTo={maxRaiseTo}
            bb={bb}
            potVal={potVal}
            toCall={toCall}
            setRaiseTo={setRaiseTo}
            doAction={doAction}
            currentBet={currentBet}
            street={street}
          />
        </div>
      </div>
    </div>
  );
}